import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, User, Calendar, CheckSquare, DollarSign } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

export default function ProjectTimeEntries({ timeEntries, tasks, users }) {
  // Group by user
  const entriesByUser = timeEntries.reduce((acc, entry) => {
    const user = entry.user_email || "Ukjent";
    if (!acc[user]) acc[user] = [];
    acc[user].push(entry);
    return acc;
  }, {});

  const getUserName = (email) => {
    const user = users.find(u => u.email === email);
    return user?.full_name || email;
  };

  const getUserRate = (email) => {
    const user = users.find(u => u.email === email);
    return user?.hourly_rate || 0;
  };

  const calculateCost = (entry) => {
    const hours = (entry.duration_minutes || 0) / 60;
    const rate = getUserRate(entry.user_email);
    return hours * rate;
  };

  const totalHours = timeEntries.reduce((sum, entry) => sum + (entry.duration_minutes || 0) / 60, 0);
  const totalCost = timeEntries.reduce((sum, entry) => sum + calculateCost(entry), 0);

  if (timeEntries.length === 0) {
    return (
      <Card className="p-8 text-center bg-white border-0 shadow-sm">
        <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">Ingen timer registrert</h3>
        <p className="text-slate-500">Start tidsregistrering for å se oversikt her</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Totalt Timer</p>
              <p className="text-2xl font-bold text-slate-900">{totalHours.toFixed(1)}h</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Kostnad</p>
              <p className="text-2xl font-bold text-slate-900">{totalCost.toLocaleString()} kr</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-lime-100 flex items-center justify-center">
              <User className="w-5 h-5 text-zinc-900" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Registreringer</p>
              <p className="text-2xl font-bold text-slate-900">{timeEntries.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Entries by user */}
      {Object.keys(entriesByUser).map((userEmail) => {
        const userEntries = entriesByUser[userEmail];
        const userHours = userEntries.reduce((sum, e) => sum + (e.duration_minutes || 0) / 60, 0);
        const userCost = userEntries.reduce((sum, e) => sum + calculateCost(e), 0);

        return (
          <Card key={userEmail} className="p-6 bg-white border-0 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-lime-100 to-lime-200 flex items-center justify-center">
                  <User className="w-5 h-5 text-zinc-900" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{getUserName(userEmail)}</h3>
                  <p className="text-sm text-slate-500">{userEmail}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-slate-900">{userHours.toFixed(1)}h</p>
                <p className="text-sm text-emerald-600">{userCost.toLocaleString()} kr</p>
              </div>
            </div>

            <div className="space-y-3">
              {userEntries
                .sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
                .map((entry) => {
                  const task = tasks.find(t => t.id === entry.task_id);
                  const hours = (entry.duration_minutes || 0) / 60;
                  const cost = calculateCost(entry);

                  return (
                    <div
                      key={entry.id}
                      className="flex items-start justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {task && (
                            <div className="flex items-center gap-1 text-sm text-zinc-900">
                              <CheckSquare className="w-3.5 h-3.5" />
                              <span className="font-medium">{task.title}</span>
                            </div>
                          )}
                          {entry.billable && (
                            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">
                              Fakturerbar
                            </Badge>
                          )}
                        </div>
                        {entry.description && (
                          <p className="text-sm text-slate-600 mb-2">{entry.description}</p>
                        )}
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>{format(new Date(entry.start_time), "dd.MM.yyyy HH:mm")}</span>
                          </div>
                          {entry.end_time && (
                            <span>→ {format(new Date(entry.end_time), "HH:mm")}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-sm font-semibold text-slate-900">{hours.toFixed(2)}h</p>
                        <p className="text-xs text-emerald-600">{cost.toLocaleString()} kr</p>
                      </div>
                    </div>
                  );
                })}
            </div>
          </Card>
        );
      })}
    </div>
  );
}